import { runGuardedDashboardNavigation } from '../components/routing/navigationConfirmation';
export const DASHBOARD_TABS_STORAGE_KEY = 'charitystack-dashboard-tabs';
export const DASHBOARD_TABS_STATE_EVENT = 'charitystack-dashboard-tabs-state-change';

export const createDashboardTab = (path = '/', idPrefix = 'tab') => ({
  id: `${idPrefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  path,
});

export const createDashboardFallbackTab = (path = '/') => ({
  id: `fallback:${path || '/'}`,
  path,
});

export const getDashboardPathFromLocation = (location) =>
  `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;

export const getDashboardLocationSignature = (location) =>
  `${getDashboardPathFromLocation(location)}:${location.key || ''}`;

export const createDashboardLocationFromPath = (path) => {
  const url = new URL(path || '/', window.location.origin);
  return {
    pathname: url.pathname,
    search: url.search,
    hash: url.hash,
    state: undefined,
    key: `dashboard-panel-${path || '/'}`,
  };
};

const normalizeDashboardTabsOptions = (optionsOrFallbackPath) => {
  if (typeof optionsOrFallbackPath === 'string') {
    return {
      fallbackPath: optionsOrFallbackPath,
      ensureFallback: true,
    };
  }

  return {
    fallbackPath: '/',
    ensureFallback: false,
    ...(optionsOrFallbackPath || {}),
  };
};

export const normalizeDashboardTabsState = (state, optionsOrFallbackPath = {}) => {
  const { fallbackPath, ensureFallback } = normalizeDashboardTabsOptions(optionsOrFallbackPath);

  const seenIds = new Set();
  const tabs = Array.isArray(state?.tabs)
    ? state.tabs
        .filter((tab) => tab && typeof tab.id === 'string' && typeof tab.path === 'string')
        .filter((tab) => {
          if (seenIds.has(tab.id)) return false;
          seenIds.add(tab.id);
          return true;
        })
    : [];

  if (!tabs.length) {
    if (!ensureFallback) {
      return {
        tabs: [],
        activeTabId: null,
      };
    }

    const fallbackTab = createDashboardFallbackTab(fallbackPath);
    return {
      tabs: [fallbackTab],
      activeTabId: fallbackTab.id,
    };
  }

  return {
    tabs,
    activeTabId: tabs.some((tab) => tab.id === state?.activeTabId)
      ? state.activeTabId
      : tabs[tabs.length - 1].id,
  };
};

export const readDashboardTabsState = (optionsOrFallbackPath = {}) => {
  if (typeof window === 'undefined') {
    return normalizeDashboardTabsState(null, optionsOrFallbackPath);
  }

  try {
    return normalizeDashboardTabsState(
      JSON.parse(sessionStorage.getItem(DASHBOARD_TABS_STORAGE_KEY) || 'null'),
      optionsOrFallbackPath,
    );
  } catch (_) {
    return normalizeDashboardTabsState(null, optionsOrFallbackPath);
  }
};

export const writeDashboardTabsState = (state) => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(DASHBOARD_TABS_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(
      new CustomEvent(DASHBOARD_TABS_STATE_EVENT, {
        detail: state,
      }),
    );
  } catch (_) {
    /* storage may be unavailable */
  }
};

/**
 * Open `path` as a NEW dashboard tab and navigate to it.
 *
 * This is the programmatic twin of Header.js's in-component
 * `openDashboardPathInNewTab` (the "+" button / Cmd+T / Cmd-click-an-anchor
 * paths): append a tab, make it active, then push. The ORDER is the point —
 * the tabs state must be written BEFORE `history.push`, because Header syncs
 * the ACTIVE tab's path to the current location on every location change. Push
 * first and the previously-active tab is rewritten to `path` (i.e. the old tab
 * is hijacked instead of a new one opening).
 *
 * `history` is the react-router history of the caller.
 */
export const openDashboardPathInNewTab = (history, path = '/') => runGuardedDashboardNavigation(history, () => {
  const currentState = readDashboardTabsState();
  const nextTab = createDashboardTab(path);
  const nextState = normalizeDashboardTabsState(
    {
      tabs: [...currentState.tabs, nextTab],
      activeTabId: nextTab.id,
    },
    path,
  );
  writeDashboardTabsState(nextState);
  history.push(path);
});

const getDashboardTabPathname = (path = '') => path.split('?')[0].split('#')[0];

/**
 * Close the tab sitting on `closingPath` and land on `targetPath` — focusing an
 * already-open tab for it if there is one, otherwise opening a new tab there.
 *
 * The "finish a task, return to the list" move: the wizard's own tab has served
 * its purpose, and leaving it open on a completed import is clutter the user
 * has to close by hand.
 *
 * SAME ORDER RULE as openDashboardPathInNewTab: write the tabs state BEFORE
 * `history.push`, because Header syncs the ACTIVE tab's path to the current
 * location on every location change. Push first and the tab we are about to
 * close is rewritten to `targetPath` first, so the close removes the wrong one.
 *
 * Matching is on PATHNAME, so a hub tab carrying a query string still counts as
 * open rather than spawning a duplicate.
 */
export const closeDashboardTabAndFocusPath = (history, closingPath, targetPath) => runGuardedDashboardNavigation(history, () => {
  const currentState = readDashboardTabsState();
  const closingPathname = getDashboardTabPathname(closingPath);
  const targetPathname = getDashboardTabPathname(targetPath);

  // Prefer the ACTIVE tab when several sit on the same path — that is the one
  // the user is looking at, and it is the one running this code.
  const closingTab =
    currentState.tabs.find(
      (tab) =>
        tab.id === currentState.activeTabId &&
        getDashboardTabPathname(tab.path) === closingPathname,
    ) || currentState.tabs.find((tab) => getDashboardTabPathname(tab.path) === closingPathname);

  const remainingTabs = closingTab
    ? currentState.tabs.filter((tab) => tab.id !== closingTab.id)
    : currentState.tabs;

  const existingTargetTab = remainingTabs.find(
    (tab) => getDashboardTabPathname(tab.path) === targetPathname,
  );
  const targetTab = existingTargetTab || createDashboardTab(targetPath);

  const nextState = normalizeDashboardTabsState(
    {
      tabs: existingTargetTab ? remainingTabs : [...remainingTabs, targetTab],
      activeTabId: targetTab.id,
    },
    targetPath,
  );
  writeDashboardTabsState(nextState);
  history.push(existingTargetTab ? existingTargetTab.path : targetPath);
});

export const getEffectiveDashboardTabsState = (tabsState, activePath) => {
  const normalizedState = normalizeDashboardTabsState(tabsState);
  const fallbackTab = createDashboardFallbackTab(activePath);
  const tabs = normalizedState.tabs.length ? normalizedState.tabs : [fallbackTab];
  const activeTabId = normalizedState.activeTabId || fallbackTab.id;
  const activePathTab = tabs.find((tab) => tab.path === activePath);
  const activePathBelongsToInactiveTab = activePathTab && activePathTab.id !== activeTabId;

  return {
    tabs: tabs.map((tab) =>
      tab.id === activeTabId && !activePathBelongsToInactiveTab
        ? {
            ...tab,
            path: activePath,
          }
        : tab,
    ),
    activeTabId,
  };
};
